class MonsgaSeparatedGallery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.items = [...this.querySelectorAll('[data-gallery-item]')];
    this.mainImage = this.querySelector('[data-gallery-main-image]');
    this.mainCopy = this.querySelector('[data-gallery-main-copy]');
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.dialogImage = this.querySelector('[data-gallery-dialog-image]');
    this.dialogCopy = this.querySelector('[data-gallery-dialog-copy]');
    this.index = 0;
    this.imageCache = new Map();
    this.loadToken = 0;
    this.onKeydown = this.onKeydown.bind(this);
    this.items.forEach((item) => item.addEventListener('click', () => this.setIndex(Number(item.dataset.galleryItem))));
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
    this.querySelector('[data-gallery-dialog-prev]')?.addEventListener('click', () => this.step(-1, true));
    this.querySelector('[data-gallery-dialog-next]')?.addEventListener('click', () => this.step(1, true));
    this.querySelector('[data-gallery-open]')?.addEventListener('click', () => this.openDialog());
    this.querySelector('[data-gallery-dialog-close]')?.addEventListener('click', () => this.closeDialog());
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.closeDialog();
    });
    this.addEventListener('keydown', this.onKeydown);
    this.preloadAll();
    this.setIndex(0);
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeydown);
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1, Boolean(this.dialog?.open)); }
    if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1, Boolean(this.dialog?.open)); }
    if (event.key === 'Escape' && this.dialog?.open) this.closeDialog();
  }

  setIndex(index) {
    if (!this.items.length) return;
    this.index = (index + this.items.length) % this.items.length;
    const item = this.items[this.index];
    const base = item.dataset.baseSrc;
    const copy = item.dataset.copySrc || '';
    const alt = item.dataset.imageAlt || '';
    const token = ++this.loadToken;
    this.classList.add('is-loading');
    // Remove only the copy layer while the next pair is loading. Keeping the
    // previous base image visible avoids a blank gallery, while hiding the old
    // copy prevents a stale caption from being shown over the new product.
    if (this.mainCopy) this.mainCopy.hidden = true;
    if (this.dialogCopy) this.dialogCopy.hidden = true;
    this.loadPair(base, copy).then(() => {
      if (token !== this.loadToken) return;
      if (this.mainImage) { this.mainImage.src = base; this.mainImage.alt = alt; }
      if (this.mainCopy) { this.mainCopy.hidden = !copy; if (copy) this.mainCopy.src = copy; this.mainCopy.alt = ''; }
      if (this.dialogImage) { this.dialogImage.src = base; this.dialogImage.alt = alt; }
      if (this.dialogCopy) { this.dialogCopy.hidden = !copy; if (copy) this.dialogCopy.src = copy; this.dialogCopy.alt = ''; }
      requestAnimationFrame(() => this.classList.remove('is-loading'));
    });
    this.items.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === this.index;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${this.index + 1} / ${this.items.length}`;
  }

  preloadAll() {
    this.items.slice(0, 3).forEach((item) => this.loadPair(item.dataset.baseSrc, item.dataset.copySrc));
    const idle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 900));
    idle(() => this.items.slice(2).forEach((item) => this.loadPair(item.dataset.baseSrc, item.dataset.copySrc)));
  }

  loadPair(base, copy) {
    const key = `${base}|${copy}`;
    if (this.imageCache.has(key)) return this.imageCache.get(key);
    const load = (src) => new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => resolve();
      image.onerror = () => resolve();
      image.src = src;
    });
    const promise = Promise.all([load(base), copy ? load(copy) : Promise.resolve()]);
    this.imageCache.set(key, promise);
    return promise;
  }

  step(delta, keepDialog = false) {
    this.setIndex(this.index + delta);
    if (keepDialog && this.dialog && !this.dialog.open) this.openDialog();
  }

  openDialog() {
    if (!this.dialog) return;
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    this.querySelector('[data-gallery-dialog-close]')?.focus();
  }

  closeDialog() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
  }
}

if (!customElements.get('monsga-separated-gallery')) {
  customElements.define('monsga-separated-gallery', MonsgaSeparatedGallery);
}
